/**
 * Crop timeline sorting utilities
 * Sorts crops alphabetically or chronologically within groups or globally
 */

/**
 * Sort crops alphabetically by name
 * @param {Array} crops - Array of crop objects
 * @returns {Array} Sorted crops array (new array, immutable)
 */
export function sortCropsAlphabetically(crops) {
  return [...crops].sort((a, b) => {
    const nameA = (a.name || '').toLowerCase()
    const nameB = (b.name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

/**
 * Sort crops chronologically by first phase start date
 * @param {Array} crops - Array of crop objects with phases array
 * @returns {Array} Sorted crops array (new array, immutable)
 */
export function sortCropsChronologically(crops) {
  return [...crops].sort((a, b) => {
    // Get first phase start date for each crop
    const startA = a.phases?.[0]?.start ?? Infinity
    const startB = b.phases?.[0]?.start ?? Infinity
    return startA - startB
  })
}

/**
 * Apply sorting to groups of crops
 * Sorts crops within each group independently
 *
 * @param {Array} groups - Array of group objects from groupCropsByStrategy
 * @param {string} sortStrategy - 'none' | 'alphabetical' | 'chronological'
 * @returns {Array} Groups with sorted crops (new array, immutable)
 */
export function applySortingToGroups(groups, sortStrategy = 'none') {
  if (!groups || groups.length === 0 || sortStrategy === 'none') {
    return groups
  }

  const sortFn = sortStrategy === 'alphabetical'
    ? sortCropsAlphabetically
    : sortCropsChronologically

  // Sort crops within each group
  return groups.map(group => ({
    ...group,
    crops: sortFn(group.crops)
  }))
}
